import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Col, Dropdown, Form, Modal, Row, Spinner, Table, Badge, Alert, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faFileImport, faCheck, faPen, faTrash, faEyeSlash, faTriangleExclamation, faRotate, faRightLeft, faTags, faCodeBranch, faRotateLeft,
} from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';
import { useConti } from '../../components/conti/ContiLayout';
import { useAuth } from '../../context/AuthContext';
import { PERSONE, SCOPE_TEMA, dataIt, euro } from '../../components/conti/contiTheme';
import {
  classificaMovimento, classificaMovimentiMultipli, convertiMovimento, convertiMovimentiMultipli, creaMovimento, eliminaMovimento, getFormatiImport, getMovimenti, getPiano, getRapporti,
  ignoraMovimenti, importaMovimenti, getDivisione, dividiMovimento, riapriMovimento, riapriMovimenti, ignoraIncassiOrdini, segnaRiservati, getContoTraSoci, messaggioErrore, modificaMovimento, registraMovimenti, getSaldiSoci, getSuggerimentiDescrizioni,
} from '../../services/contiService';

const LIMITE = 50;
// Movimenti senza voce del piano dei conti: spostano denaro tra rapporti
const TRASFERIMENTI = ['giroconto', 'rimborso_socio', 'restituzione_socio', 'conferimento_socio', 'prelievo_utili', 'finanziamento_socio'];
const isTrasf = (segno) => TRASFERIMENTI.includes(segno);
const isSoci = (segno) => ['rimborso_socio', 'restituzione_socio', 'conferimento_socio', 'prelievo_utili', 'finanziamento_socio'].includes(segno);
// Da quale contabilita' parte e dove arriva il denaro nei movimenti con i soci
const SCOPE_ORIGINE = { rimborso_socio: 'azienda', prelievo_utili: 'azienda', restituzione_socio: 'personale', conferimento_socio: 'personale', finanziamento_socio: 'personale' };
const SCOPE_DESTINAZIONE = { rimborso_socio: 'personale', prelievo_utili: 'personale', restituzione_socio: 'azienda', conferimento_socio: 'azienda', finanziamento_socio: 'azienda' };
const SPIEGA_SOCI = {
  rimborso_socio: 'Rimborso degli anticipi: l’azienda ridà al socio soldi che aveva anticipato per lei. Riduce il debito dell’azienda verso il socio (con la scrittura in Prima Nota).',
  prelievo_utili: 'Prelievo del socio: denaro dell’azienda che passa al socio, ad esempio contanti versati sul suo conto. Aumenta quanto il socio deve all’azienda come prelievi (con la scrittura in Prima Nota). Se poi il socio paga spese dell’azienda con quel denaro, registrale come anticipi.',
  restituzione_socio: 'Restituzione di prelievi: il socio ripiana soldi che l’azienda aveva già pagato per lui. Riduce quanto deve all’azienda (con la scrittura in Prima Nota).',
  finanziamento_socio: 'Anticipo del socio: il socio presta denaro all’azienda (ad esempio Francesca ti gira soldi che usi per ModenaCinema). Aumenta il debito dell’azienda verso il socio, che potrà essere rimborsato (con la scrittura in Prima Nota, senza toccare il capitale).',
  conferimento_socio: 'Conferimento: il socio versa denaro nell’azienda come apporto di capitale, non come debito e non come ricavo (con la scrittura in Prima Nota).',
};
const ETICHETTA_SEGNO = {
  giroconto: 'Giroconto', rimborso_socio: 'Rimborso socio', restituzione_socio: 'Restituzione socio',
  conferimento_socio: 'Conferimento socio', prelievo_utili: 'Prelievo del socio', finanziamento_socio: 'Anticipo del socio',
};
const TAB = [
  { k: 'da_classificare', l: 'Da classificare' },
  { k: 'classificato', l: 'Pronti da registrare' },
  { k: 'registrato', l: 'Registrati' },
  { k: 'ignorato', l: 'Ignorati' },
  { k: 'riservato', l: '🔒 Riservati', soloProprietario: true },
  { k: '', l: 'Tutti' },
];
const oggi = () => new Date().toISOString().slice(0, 10);
const VUOTO = {
  scope: 'azienda', segno: 'uscita', banca_id: '', banca_destinazione_id: '', data: oggi(), importo: '', commissione: '',
  descrizione: '', controparte: '', piano_conti_id: '', persona: '', note: '', ha_fattura: false,
};

const SelettoreScope = ({ valore, onChange, disabled }) => (
  <div className="d-inline-flex" style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid #ced4da' }}>
    {['azienda', 'personale'].map((s) => (
      <button key={s} type="button" disabled={disabled} onClick={() => onChange(s)}
        style={{
          border: 0, padding: '3px 9px', fontSize: 12, fontWeight: 600, cursor: disabled ? 'default' : 'pointer',
          background: valore === s ? SCOPE_TEMA[s].colore : '#fff', color: valore === s ? '#fff' : '#495057',
        }}>
        {s === 'azienda' ? 'Azienda' : 'Personale'}
      </button>
    ))}
  </div>
);

const Movimenti = () => {
  const { scope, tema, aggiornaConteggi } = useConti();
  const [tab, setTab] = useState('da_classificare');
  const [soloScope, setSoloScope] = useState(false);
  const [cerca, setCerca] = useState('');
  const [bancaFiltro, setBancaFiltro] = useState('');
  const [vociFiltro, setVociFiltro] = useState([]);  // id voci del piano; 0 = senza voce
  const [pagina, setPagina] = useState(0);
  const [dati, setDati] = useState({ movimenti: [], total: 0 });
  const [caricamento, setCaricamento] = useState(true);
  const [rapporti, setRapporti] = useState([]);
  const [piano, setPiano] = useState({ azienda: [], personale: [] });
  const [selezione, setSelezione] = useState([]);
  const [selezioneReg, setSelezioneReg] = useState([]);
  const { user } = useAuth();
  const proprietario = !!(user && user.is_owner);
  const [form, setForm] = useState(null);
  const [salvataggio, setSalvataggio] = useState(false);
  const [importa, setImporta] = useState(null);
  const [formati, setFormati] = useState([]);
  const [saldiSoci, setSaldiSoci] = useState(null);
  const [espansi, setEspansi] = useState([]);
  const [converti, setConverti] = useState(null);
  const [dividi, setDividi] = useState(null);
  const [traSoci, setTraSoci] = useState(null);
  const [traSociAperto, setTraSociAperto] = useState(false);
  const [bulk, setBulk] = useState(null);
  const [inModifica, setInModifica] = useState(null);

  const carica = useCallback(async () => {
    setCaricamento(true);
    try {
      const r = await getMovimenti({
        scope: soloScope ? scope : undefined, stato: tab || undefined, banca_id: bancaFiltro || undefined,
        voci: vociFiltro.length ? vociFiltro.join(',') : undefined, cerca: cerca || undefined, skip: pagina * LIMITE, limit: LIMITE,
      });
      setDati(r);
      getContoTraSoci().then(setTraSoci).catch(() => {});
      setSelezione([]);
      setSelezioneReg([]);
    } catch (e) {
      toast.error(messaggioErrore(e, 'Impossibile caricare i movimenti'));
    } finally {
      setCaricamento(false);
    }
  }, [soloScope, scope, tab, bancaFiltro, vociFiltro, cerca, pagina]);

  useEffect(() => { carica(); }, [carica]);
  useEffect(() => { setPagina(0); }, [tab, soloScope, scope, bancaFiltro, vociFiltro, cerca]);

  useEffect(() => {
    Promise.all([getRapporti('azienda'), getRapporti('personale'), getPiano('azienda', true), getPiano('personale', true), getFormatiImport()])
      .then(([ra, rp, pa, pp, f]) => {
        setRapporti([...ra.rapporti, ...rp.rapporti].filter((r) => r.attivo));
        setPiano({ azienda: pa, personale: pp });
        setFormati(f);
      })
      .catch((e) => toast.error(messaggioErrore(e, 'Impossibile caricare rapporti e voci')));
  }, []);

  useEffect(() => {
    if (form && isSoci(form.segno)) {
      getSaldiSoci().then(setSaldiSoci).catch(() => setSaldiSoci(null));
    }
  }, [form && form.segno]); // eslint-disable-line react-hooks/exhaustive-deps

  const [suggerimenti, setSuggerimenti] = useState([]);
  const formNuovo = !!form && !form.id;
  const scopeForm = form ? form.scope : null;
  useEffect(() => {
    if (!formNuovo) return;
    getSuggerimentiDescrizioni(scopeForm).then(setSuggerimenti).catch(() => setSuggerimenti([]));
  }, [formNuovo, scopeForm]);

  // Scelta di una descrizione gia' usata: precompila voce, persona, controparte e rapporto se ancora vuoti
  const cambiaDescrizione = (testo) => {
    const s = formNuovo ? suggerimenti.find((x) => x.descrizione === testo) : null;
    if (!s) { setForm({ ...form, descrizione: testo }); return; }
    const voceValida = (piano[form.scope] || []).some((v) => v.id === s.piano_conti_id);
    setForm({
      ...form, descrizione: testo,
      controparte: form.controparte || s.controparte || '',
      piano_conti_id: form.piano_conti_id || (voceValida ? s.piano_conti_id : ''),
      persona: form.persona || s.persona || '',
      banca_id: form.banca_id || s.banca_id || '',
    });
  };

  const nuoveRighe = importa && importa.risultato ? importa.risultato.anteprima.filter((r) => r.esito === 'nuovo') : [];

  const salvaDescrizione = async () => {
    if (!inModifica) return;
    const { id, testo } = inModifica;
    setInModifica(null);
    const originale = dati.movimenti.find((x) => x.id === id);
    if (!originale || testo.trim() === originale.descrizione || !testo.trim()) return;
    try {
      await modificaMovimento(id, { descrizione: testo.trim() });
      ricarica();
    } catch (e) {
      toast.error(messaggioErrore(e));
    }
  };

  const selezionaSimili = async (m) => {
    try {
      const r = await getMovimenti({ cerca: m.controparte, stato: tab || undefined, limit: 500 });
      const ids = r.movimenti
        .filter((x) => x.controparte === m.controparte && x.stato !== 'registrato' && x.stato !== 'ignorato' && !isTrasf(x.segno))
        .map((x) => x.id);
      setSelezione(ids);
      toast.success(`${ids.length} movimenti con «${m.controparte}» selezionati`);
    } catch (e) {
      toast.error(messaggioErrore(e));
    }
  };

  const apriConvertiMultiplo = () => {
    const scelti = dati.movimenti.filter((x) => selezione.includes(x.id));
    if (scelti.some((x) => x.segno !== 'entrata' && x.segno !== 'uscita')) {
      toast.error('Tra i movimenti scelti ce ne sono alcuni già giroconti o spostamenti: togli la spunta.');
      return;
    }
    if (new Set(scelti.map((x) => `${x.segno}-${x.banca_id}`)).size > 1) {
      toast.error('Per convertirli insieme scegli movimenti dello stesso rapporto e dello stesso verso (tutte entrate o tutte uscite).');
      return;
    }
    setSaldiSoci(null);
    getSaldiSoci().then(setSaldiSoci).catch(() => {});
    setConverti({ m: scelti[0], multipli: scelti, altra: '', persona: '', natura: '' });
  };

  const apriBulk = () => {
    const noti = dati.movimenti.filter((x) => selezione.includes(x.id));
    setBulk({ scope: '', voce: '', persona: '', noti });
  };

  const salvaBulk = async (e) => {
    e.preventDefault();
    const payload = { ids: selezione };
    if (bulk.scope) payload.scope = bulk.scope;
    if (bulk.voce) payload.piano_conti_id = Number(bulk.voce);
    if (bulk.persona) payload.persona = bulk.persona;
    try {
      const r = await classificaMovimentiMultipli(payload);
      toast.success(`${r.aggiornati} movimenti aggiornati`);
      if (r.aggiornati > r.pronti) toast(`${r.aggiornati - r.pronti} restano da classificare: manca la voce o la persona`, { icon: 'ℹ️', duration: 6000 });
      (r.errori || []).slice(0, 3).forEach((x) => toast.error(`#${x.id}: ${x.errore}`, { duration: 7000 }));
      setBulk(null);
      ricarica();
    } catch (err) {
      toast.error(messaggioErrore(err));
    }
  };

  const rapportoPer = useMemo(() => Object.fromEntries(rapporti.map((r) => [r.id, r])), [rapporti]);
  const ricarica = () => { carica(); aggiornaConteggi(); };

  const classifica = async (m, cambi) => {
    try {
      await classificaMovimento(m.id, cambi);
      ricarica();
    } catch (e) {
      toast.error(messaggioErrore(e));
      carica();
    }
  };

  const registra = async (ids) => {
    try {
      const r = await registraMovimenti(ids);
      if (r.registrati) toast.success(`${r.registrati} moviment${r.registrati === 1 ? 'o registrato' : 'i registrati'} su Firefly`);
      (r.errori || []).forEach((x) => toast.error(`#${x.id}: ${x.errore}`, { duration: 7000 }));
      ricarica();
    } catch (e) {
      toast.error(messaggioErrore(e));
    }
  };

  const ignora = async (ids) => {
    try {
      await ignoraMovimenti(ids);
      ricarica();
    } catch (e) {
      toast.error(messaggioErrore(e));
    }
  };

  const apriDividi = async (m) => {
    try {
      const d = await getDivisione(m.id);
      const persona = m.persona && m.persona !== 'Comune' ? m.persona : '';
      const parti = d.parti.length > 1
        ? d.parti.map((p) => ({ importo: String(p.importo), persona: p.persona || '', piano_conti_id: p.piano_conti_id || '' }))
        : [{ importo: String(d.totale), persona, piano_conti_id: m.piano_conti_id || '' }, { importo: '', persona: '', piano_conti_id: m.piano_conti_id || '' }];
      setDividi({ m, totale: d.totale, parti, registrati: d.parti.some((p) => p.stato === 'registrato') });
    } catch (e) {
      toast.error(messaggioErrore(e));
    }
  };

  const segnaSelezione = async (riservato) => {
    const ids = [...selezione, ...selezioneReg];
    if (riservato && selezioneReg.length > 0
        && !window.confirm(`${selezioneReg.length} movimenti sono già registrati: la loro registrazione su Firefly verrà annullata e spariranno anche dai saldi. Continuare?`)) return;
    try {
      const r = await segnaRiservati(ids, riservato);
      toast.success(riservato ? `${r.aggiornati} movimenti resi riservati` : `${r.aggiornati} movimenti tolti dai riservati`);
      (r.errori || []).slice(0, 3).forEach((x) => toast.error(`#${x.id}: ${x.errore}`, { duration: 7000 }));
      ricarica();
    } catch (e) {
      toast.error(messaggioErrore(e));
    }
  };

  const riapriSelezione = async () => {
    if (!window.confirm(`Annullare la registrazione di ${selezioneReg.length} movimenti su Firefly e in Prima Nota? Torneranno modificabili e potrai registrarli di nuovo.`)) return;
    try {
      const r = await riapriMovimenti(selezioneReg);
      if (r.riaperti) toast.success(`${r.riaperti} registrazioni annullate`);
      (r.errori || []).slice(0, 3).forEach((x) => toast.error(`#${x.id}: ${x.errore}`, { duration: 7000 }));
      ricarica();
    } catch (e) {
      toast.error(messaggioErrore(e));
    }
  };

  const riapri = async (m) => {
    if (!window.confirm('Annullare la registrazione su Firefly e in Prima Nota? Il movimento tornerà modificabile e potrai registrarlo di nuovo.')) return;
    try {
      await riapriMovimento(m.id);
      toast.success('Registrazione annullata');
      ricarica();
    } catch (e) {
      toast.error(messaggioErrore(e));
    }
  };

  const elimina = async (m) => {
    if (!window.confirm(`Eliminare il movimento "${m.descrizione}"?`)) return;
    try {
      await eliminaMovimento(m.id);
      ricarica();
    } catch (e) {
      toast.error(messaggioErrore(e));
    }
  };

  const apriForm = (m) => {
    if (m) {
      setForm({
        id: m.id, scope: m.scope, segno: m.segno, banca_id: m.banca_id, banca_destinazione_id: m.banca_destinazione_id || '',
        data: m.data, importo: m.importo, commissione: m.commissione || '', descrizione: m.descrizione,
        controparte: m.controparte || '', piano_conti_id: m.piano_conti_id || '', persona: m.persona || '', note: m.note || '',
        ha_fattura: !!m.ha_fattura,
      });
    } else {
      setForm({ ...VUOTO, scope });
    }
  };

  const salva = async (e) => {
    e.preventDefault();
    setSalvataggio(true);
    const num = (v) => (v === '' || v === null ? null : parseFloat(String(v).replace(',', '.')));
    const payload = {
      scope: form.scope, segno: form.segno, banca_id: Number(form.banca_id), data: form.data, importo: num(form.importo),
      commissione: num(form.commissione), descrizione: form.descrizione, controparte: form.controparte || null,
      piano_conti_id: isTrasf(form.segno) || !form.piano_conti_id ? null : Number(form.piano_conti_id),
      persona: form.persona || null, note: form.note || null,
      ha_fattura: form.scope === 'azienda' && bancaForm && bancaForm.scope === 'personale' ? !!form.ha_fattura : false,
      banca_destinazione_id: isTrasf(form.segno) && form.banca_destinazione_id ? Number(form.banca_destinazione_id) : null,
    };
    try {
      if (form.id) await modificaMovimento(form.id, payload); else await creaMovimento(payload);
      toast.success(form.id ? 'Movimento aggiornato' : 'Movimento inserito');
      setForm(null);
      ricarica();
    } catch (err) {
      toast.error(messaggioErrore(err, 'Salvataggio non riuscito'));
    } finally {
      setSalvataggio(false);
    }
  };

  const bancaForm = form ? rapportoPer[Number(form.banca_id)] : null;
  const servePersona = form && (form.scope === 'personale' || (bancaForm && bancaForm.scope !== form.scope));
  const selezionabili = dati.movimenti.filter((m) => m.stato !== 'registrato');
  const registratiPagina = dati.movimenti.filter((m) => m.stato === 'registrato');
  const tuttiSel = selezionabili.length > 0
    ? selezionabili.every((m) => selezione.includes(m.id))
    : registratiPagina.length > 0 && registratiPagina.every((m) => selezioneReg.includes(m.id));
  const pronti = selezione.filter((id) => dati.movimenti.find((m) => m.id === id)?.stato === 'classificato');

  const badgeStato = (m) => {
    const mappa = { da_classificare: ['warning', m.piano_conti_id ? 'Manca la persona' : 'Da classificare'], classificato: ['info', 'Pronto'], registrato: ['success', 'Registrato'], ignorato: ['secondary', 'Ignorato'], riservato: ['dark', '🔒 Riservato'] };
    const [bg, t] = mappa[m.stato] || ['light', m.stato];
    return <Badge bg={bg} text={bg === 'warning' || bg === 'light' ? 'dark' : undefined}>{t}</Badge>;
  };

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div className="d-flex flex-wrap gap-1">
          {TAB.filter((t) => !t.soloProprietario || proprietario).map((t) => (
            <button key={t.k || 'tutti'} type="button" onClick={() => setTab(t.k)}
              style={{ border: 0, borderRadius: 999, padding: '6px 14px', fontWeight: 600, fontSize: 14, cursor: 'pointer',
                background: tab === t.k ? tema.colore : '#e9ecef', color: tab === t.k ? '#fff' : '#495057' }}>
              {t.l}
            </button>
          ))}
        </div>
        <div className="d-flex gap-2">
          {dati.movimenti.some((m) => m.ordini_registrati) && (
            <Button variant="outline-info" title="Ignora gli incassi già registrati con gli ordini del negozio"
              onClick={async () => {
                if (!window.confirm('Ignorare tutti gli incassi che coincidono con ordini già registrati su Firefly? Verranno spostati nella scheda «Ignorati».')) return;
                try {
                  const r = await ignoraIncassiOrdini();
                  toast.success(`${r.ignorati} incassi ignorati`);
                  ricarica();
                } catch (e) {
                  toast.error(messaggioErrore(e));
                }
              }}>
              Ignora incassi già negli ordini
            </Button>
          )}
          <Button variant="outline-secondary" onClick={ricarica}><FontAwesomeIcon icon={faRotate} /></Button>
          <Button variant="outline-primary" onClick={() => setImporta({ bancaId: '', fonte: '', file: null, risultato: null, esclusi: [], inclusi: [], lavoro: false })}>
            <FontAwesomeIcon icon={faFileImport} className="me-2" />Importa da file
          </Button>
          <Button style={{ background: tema.colore, borderColor: tema.colore }} onClick={() => apriForm(null)}>
            <FontAwesomeIcon icon={faPlus} className="me-2" />Nuovo movimento
          </Button>
        </div>
      </div>

      {traSoci && traSoci.movimenti.length > 0 && (
        <div className="mb-3" style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,.08)', padding: '10px 16px' }}>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <strong>Tu e Francesca</strong>{' '}
              {traSoci.giorgio_deve_a_francesca === 0 ? 'siete in pari'
                : traSoci.giorgio_deve_a_francesca > 0 ? <>Giorgio deve a Francesca <strong>{euro(traSoci.giorgio_deve_a_francesca)}</strong></>
                  : <>Francesca deve a Giorgio <strong>{euro(-traSoci.giorgio_deve_a_francesca)}</strong></>}
              {traSoci.da_registrare > 0 && (
                <span className="text-muted" style={{ fontSize: 13 }}> · con i {traSoci.da_registrare} da registrare: {traSoci.dopo_le_registrazioni >= 0
                  ? `Giorgio deve ${euro(traSoci.dopo_le_registrazioni)}` : `Francesca deve ${euro(-traSoci.dopo_le_registrazioni)}`}</span>
              )}
            </div>
            <Button size="sm" variant="link" onClick={() => setTraSociAperto(!traSociAperto)}>{traSociAperto ? 'Nascondi' : 'Come si calcola'}</Button>
          </div>
          {traSociAperto && (
            <div className="mt-2">
              <div className="text-muted mb-2" style={{ fontSize: 12 }}>
                Conta i soldi passati da un tuo conto a uno di Francesca (o viceversa), compresi gli anticipi finanziati con il conto dell’altro e i rimborsi
                incassati sul conto dell’altro. Non conta le spese comuni di casa.
              </div>
              <Table size="sm" className="mb-0" style={{ fontSize: 13 }}>
                <tbody>
                  {traSoci.movimenti.map((v) => (
                    <tr key={v.id} style={{ opacity: v.registrato ? 1 : 0.55 }}>
                      <td style={{ whiteSpace: 'nowrap' }}>{dataIt(v.data)}</td>
                      <td>{v.descrizione}<div className="text-muted" style={{ fontSize: 11 }}>{v.motivo}{v.registrato ? '' : ' (da registrare)'}</div></td>
                      <td className="text-end" style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                        {v.debitore === 'Giorgio' ? '+' : '−'}{euro(v.importo)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <div className="text-muted mt-1" style={{ fontSize: 11 }}>+ aumenta quanto Giorgio deve a Francesca, − lo riduce.</div>
            </div>
          )}
        </div>
      )}

      <Row className="g-2 mb-3 align-items-center">
        <Col md={4}><Form.Control placeholder="Cerca in descrizione o controparte…" value={cerca} onChange={(e) => setCerca(e.target.value)} /></Col>
        <Col md={3}>
          <Form.Select value={bancaFiltro} onChange={(e) => setBancaFiltro(e.target.value)}>
            <option value="">Tutti i rapporti</option>
            {rapporti.map((r) => <option key={r.id} value={r.id}>{r.scope === 'azienda' ? '🏢' : '🏠'} {r.denominazione}</option>)}
          </Form.Select>
        </Col>
        <Col md="auto">
          <Dropdown autoClose="outside">
            <Dropdown.Toggle variant={vociFiltro.length ? 'primary' : 'outline-secondary'} id="filtro-voci">
              {vociFiltro.length ? `Voci: ${vociFiltro.length} scelte` : 'Tutte le voci'}
            </Dropdown.Toggle>
            <Dropdown.Menu style={{ maxHeight: 420, overflowY: 'auto', minWidth: 280, padding: '8px 12px' }}>
              <div className="d-flex justify-content-between mb-2">
                <Button size="sm" variant="link" className="p-0" onClick={() => setVociFiltro([])}>Togli filtro</Button>
              </div>
              <Form.Check id="voce-0" type="checkbox" label="— senza voce —" checked={vociFiltro.includes(0)}
                onChange={() => setVociFiltro((v) => (v.includes(0) ? v.filter((x) => x !== 0) : [...v, 0]))} />
              {['azienda', 'personale'].map((sc) => (
                <div key={sc}>
                  <div className="text-muted mt-2 mb-1" style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase' }}>
                    {sc === 'azienda' ? '🏢 Azienda' : '🏠 Personale'}
                  </div>
                  {(piano[sc] || []).map((v) => (
                    <Form.Check key={v.id} id={`voce-${v.id}`} type="checkbox" label={v.nome} checked={vociFiltro.includes(v.id)}
                      onChange={() => setVociFiltro((sel) => (sel.includes(v.id) ? sel.filter((x) => x !== v.id) : [...sel, v.id]))} />
                  ))}
                </div>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </Col>
        <Col md="auto"><Form.Check type="switch" id="solo-scope" label={`Solo ${tema.nome}`} checked={soloScope} onChange={(e) => setSoloScope(e.target.checked)} /></Col>
        {(selezione.length > 0 || selezioneReg.length > 0) && (
          <Col className="d-flex gap-2 justify-content-md-end">
            {selezioneReg.length > 0 && (
              <Button size="sm" variant="outline-danger" onClick={riapriSelezione}>
                <FontAwesomeIcon icon={faRotateLeft} className="me-1" />Annulla registrazione ({selezioneReg.length})
              </Button>
            )}
            {proprietario && tab === 'riservato' && selezione.length > 0 && (
              <Button size="sm" variant="outline-dark" onClick={() => segnaSelezione(false)}>Togli dai riservati ({selezione.length})</Button>
            )}
            {proprietario && tab !== 'riservato' && (selezione.length + selezioneReg.length) > 0 && (
              <Button size="sm" variant="dark" onClick={() => segnaSelezione(true)}>🔒 Segna come riservati ({selezione.length + selezioneReg.length})</Button>
            )}
            {tab !== 'riservato' && selezione.length > 0 && (<>
            <Button size="sm" variant="info" onClick={apriConvertiMultiplo}>
              <FontAwesomeIcon icon={faRightLeft} className="me-1" />Converti in giroconto ({selezione.length})
            </Button>
            <Button size="sm" style={{ background: tema.colore, borderColor: tema.colore }} onClick={apriBulk}>
              <FontAwesomeIcon icon={faTags} className="me-1" />Assegna voce e persona ({selezione.length})
            </Button>
            <Button size="sm" variant="success" disabled={pronti.length === 0} onClick={() => registra(pronti)}>
              <FontAwesomeIcon icon={faCheck} className="me-1" />Registra {pronti.length}
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={() => ignora(selezione)}>
              <FontAwesomeIcon icon={faEyeSlash} className="me-1" />Ignora {selezione.length}
            </Button>
            </>)}
          </Col>
        )}
      </Row>

      <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 2px 10px rgba(0,0,0,.06)', overflow: 'hidden' }}>
        <Table hover responsive className="mb-0 align-middle" style={{ fontSize: 14 }}>
          <thead style={{ background: tema.morbido }}>
            <tr>
              <th style={{ width: 34 }}>
                <Form.Check checked={tuttiSel} onChange={(e) => (selezionabili.length > 0
                  ? setSelezione(e.target.checked ? selezionabili.map((m) => m.id) : [])
                  : setSelezioneReg(e.target.checked ? registratiPagina.map((m) => m.id) : []))} />
              </th>
              <th>Data</th><th>Movimento</th><th>Rapporto</th><th className="text-end">Importo</th>
              <th>Contabilità</th><th>Voce</th><th>Persona</th><th>Stato</th><th></th>
            </tr>
          </thead>
          <tbody>
            {caricamento && <tr><td colSpan={10} className="text-center py-4"><Spinner animation="border" size="sm" /></td></tr>}
            {!caricamento && dati.movimenti.length === 0 && (
              <tr><td colSpan={10} className="text-center text-muted py-5">Nessun movimento. Inseriscine uno o importa un file.</td></tr>
            )}
            {!caricamento && dati.movimenti.map((m) => {
              const bloccato = m.stato === 'registrato' || m.stato === 'ignorato';
              const vociScope = piano[m.scope] || [];
              const servePers = m.scope === 'personale' || m.banca_scope !== m.scope || isSoci(m.segno);
              return (
                <tr key={m.id}>
                  <td>{m.stato === 'registrato' ? (
                    <Form.Check checked={selezioneReg.includes(m.id)} title="Seleziona per annullare la registrazione"
                      onChange={(e) => setSelezioneReg((s) => (e.target.checked ? [...s, m.id] : s.filter((x) => x !== m.id)))} />
                  ) : (
                    <Form.Check checked={selezione.includes(m.id)}
                      onChange={(e) => setSelezione((s) => (e.target.checked ? [...s, m.id] : s.filter((x) => x !== m.id)))} />
                  )}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{dataIt(m.data)}</td>
                  <td style={{ minWidth: 260, maxWidth: 440 }}>
                    {inModifica && inModifica.id === m.id ? (
                      <Form.Control size="sm" autoFocus value={inModifica.testo}
                        onChange={(e) => setInModifica({ id: m.id, testo: e.target.value })}
                        onBlur={salvaDescrizione}
                        onKeyDown={(e) => { if (e.key === 'Enter') salvaDescrizione(); if (e.key === 'Escape') setInModifica(null); }} />
                    ) : (
                      <div className="d-flex align-items-start gap-1">
                        <div
                          onClick={() => setEspansi((e) => (e.includes(m.id) ? e.filter((x) => x !== m.id) : [...e, m.id]))}
                          title={m.note ? `${m.descrizione}\n\n${m.note}` : m.descrizione}
                          style={{
                            cursor: 'pointer', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.3, flexGrow: 1,
                            ...(espansi.includes(m.id) ? {} : { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }),
                          }}
                        >
                          {m.descrizione}
                        </div>
                        {!bloccato && (
                          <span role="button" title="Modifica la descrizione" className="text-muted" style={{ cursor: 'pointer', fontSize: 12, paddingTop: 2 }}
                            onClick={() => setInModifica({ id: m.id, testo: m.descrizione })}>
                            <FontAwesomeIcon icon={faPen} />
                          </span>
                        )}
                      </div>
                    )}
                    {espansi.includes(m.id) && m.note && (
                      <div className="text-muted mt-1" style={{ fontSize: 12, whiteSpace: 'normal', wordBreak: 'break-word' }}>{m.note}</div>
                    )}
                    {m.controparte && (
                      <div className="text-muted text-truncate" style={{ fontSize: 12, cursor: bloccato || isTrasf(m.segno) ? 'default' : 'pointer', textDecoration: bloccato || isTrasf(m.segno) ? 'none' : 'underline dotted' }}
                        title={bloccato || isTrasf(m.segno) ? '' : 'Clic: seleziona tutti i movimenti con questa controparte'}
                        onClick={() => { if (!bloccato && !isTrasf(m.segno)) selezionaSimili(m); }}>
                        {m.controparte}
                      </div>
                    )}
                    {m.possibile_giroconto && (m.segno === 'entrata' || m.segno === 'uscita') && (
                      <Badge bg="info" title={`Sembra uno spostamento tra tuoi conti (${m.possibile_giroconto})`}>forse giroconto</Badge>
                    )}
                    {m.ordine_non_registrato && (
                      <Badge bg="secondary" title="Questo incasso corrisponde a un ordine concluso che NON è registrato su Firefly (gli ordini del sito pagati online non lo sono): l’incasso va tenuto e registrato da qui">
                        ordine n. {m.ordine_non_registrato.ordine}{m.ordine_non_registrato.probabile ? ' (probabile)' : ''} · non registrato: tieni l’incasso
                      </Badge>
                    )}
                    {m.ordini_registrati && <Badge bg="info" title="L’incasso è già stato registrato da questi ordini: registrarlo ancora lo conterebbe due volte">già nell’ordine n. {m.ordini_registrati.join(', ')}</Badge>}
                    {isTrasf(m.segno) && <div className="text-muted" style={{ fontSize: 12 }}>→ {m.banca_destinazione}</div>}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <span style={{ fontSize: 11 }}>{m.banca_scope === 'azienda' ? '🏢' : '🏠'}</span> {m.banca}
                  </td>
                  <td className="text-end" style={{ fontWeight: 700, whiteSpace: 'nowrap',
                    color: m.segno === 'entrata' ? '#2f9e44' : m.segno === 'uscita' ? '#e03131' : '#868e96' }}>
                    {m.segno === 'uscita' ? '−' : m.segno === 'entrata' ? '+' : ''}{euro(m.importo)}
                    {m.commissione ? <div className="text-muted" style={{ fontSize: 11, fontWeight: 400 }}>comm. {euro(m.commissione)}</div> : null}
                  </td>
                  <td><SelettoreScope valore={m.scope} disabled={bloccato || isSoci(m.segno)} onChange={(s) => classifica(m, { scope: s, piano_conti_id: null })} /></td>
                  <td style={{ minWidth: 170 }}>
                    {isTrasf(m.segno) ? <span className="text-muted">{ETICHETTA_SEGNO[m.segno]}</span> : (
                      <Form.Select size="sm" disabled={bloccato} value={m.piano_conti_id || ''}
                        onChange={(e) => classifica(m, { piano_conti_id: e.target.value ? Number(e.target.value) : null })}>
                        <option value="">— scegli —</option>
                        {vociScope.filter((v) => (m.segno === 'entrata' ? v.tipo === 'entrata' : v.tipo === 'spesa')).map((v) => (
                          <option key={v.id} value={v.id}>{v.nome}</option>
                        ))}
                      </Form.Select>
                    )}
                  </td>
                  <td style={{ minWidth: 110 }}>
                    {servePers ? (
                      <Form.Select size="sm" disabled={bloccato} value={m.persona || ''} isInvalid={!bloccato && !!m.piano_conti_id && !m.persona}
                        onChange={(e) => classifica(m, { persona: e.target.value || null })}>
                        <option value="">—</option>
                        {PERSONE.map((p) => <option key={p} value={p}>{p}</option>)}
                      </Form.Select>
                    ) : <span className="text-muted">—</span>}
                  </td>
                  <td>
                    {badgeStato(m)}
                    {m.errore_registrazione && (
                      <OverlayTrigger overlay={<Tooltip>{m.errore_registrazione}</Tooltip>}>
                        <span className="ms-1 text-danger"><FontAwesomeIcon icon={faTriangleExclamation} /></span>
                      </OverlayTrigger>
                    )}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {m.stato === 'classificato' && (
                      <Button size="sm" variant="success" className="me-1" title="Registra su Firefly" onClick={() => registra([m.id])}>
                        <FontAwesomeIcon icon={faCheck} />
                      </Button>
                    )}
                    {m.stato === 'registrato' && (
                      <>
                        <Button size="sm" variant="light" className="me-1" title="Rettifica: dividi tra più quote (Giorgio / Francesca) e ripeti la registrazione" onClick={() => apriDividi(m)}>
                          <FontAwesomeIcon icon={faCodeBranch} />
                        </Button>
                        <Button size="sm" variant="light" title="Annulla la registrazione per modificare il movimento" onClick={() => riapri(m)}>
                          <FontAwesomeIcon icon={faRotateLeft} />
                        </Button>
                      </>
                    )}
                    {m.stato !== 'registrato' && (
                      <>
                        {m.stato !== 'ignorato' && (
                          <Button size="sm" variant={m.diviso ? 'warning' : 'light'} className="me-1" title="Dividi in più quote" onClick={() => apriDividi(m)}>
                            <FontAwesomeIcon icon={faCodeBranch} />
                          </Button>
                        )}
                        {(m.segno === 'entrata' || m.segno === 'uscita') && m.stato !== 'ignorato' && (
                          <Button size="sm" variant={m.possibile_giroconto ? 'info' : 'light'} className="me-1" title="Converti in giroconto o spostamento tra rapporti"
                            onClick={() => { setSaldiSoci(null); getSaldiSoci().then(setSaldiSoci).catch(() => {}); setConverti({ m, altra: '', persona: m.persona && m.persona !== 'Comune' ? m.persona : '', natura: '' }); }}>
                            <FontAwesomeIcon icon={faRightLeft} />
                          </Button>
                        )}
                        <Button size="sm" variant="light" className="me-1" onClick={() => apriForm(m)}><FontAwesomeIcon icon={faPen} /></Button>
                        <Button size="sm" variant="light" onClick={() => elimina(m)}><FontAwesomeIcon icon={faTrash} /></Button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>

      {dati.total > LIMITE && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <span className="text-muted">{pagina * LIMITE + 1}–{Math.min((pagina + 1) * LIMITE, dati.total)} di {dati.total}</span>
          <div className="d-flex gap-2">
            <Button size="sm" variant="outline-secondary" disabled={pagina === 0} onClick={() => setPagina(pagina - 1)}>Precedenti</Button>
            <Button size="sm" variant="outline-secondary" disabled={(pagina + 1) * LIMITE >= dati.total} onClick={() => setPagina(pagina + 1)}>Successivi</Button>
          </div>
        </div>
      )}

      {/* ---- Nuovo / modifica movimento ---- */}
      <Modal show={!!form} onHide={() => setForm(null)} centered size="lg">
        {form && (
          <Form onSubmit={salva}>
            <Modal.Header closeButton style={{ background: SCOPE_TEMA[form.scope].morbido }}>
              <Modal.Title style={{ color: SCOPE_TEMA[form.scope].scuro }}>{form.id ? 'Modifica movimento' : 'Nuovo movimento'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="mb-3">
                {(() => {
                  const bottone = ([k, l, c]) => (
                    <button key={k} type="button" onClick={() => setForm({ ...form, segno: k, piano_conti_id: '', banca_id: '', banca_destinazione_id: '', scope: isSoci(k) ? 'azienda' : form.scope, persona: isSoci(k) && form.persona === 'Comune' ? '' : form.persona })}
                      style={{ border: `1px solid ${c}`, borderRadius: 8, padding: '5px 14px', fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap', cursor: 'pointer',
                        background: form.segno === k ? c : '#fff', color: form.segno === k ? '#fff' : c }}>{l}</button>
                  );
                  return (
                    <>
                      <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
                        <SelettoreScope valore={form.scope} disabled={isSoci(form.segno)} onChange={(s) => setForm({ ...form, scope: s, piano_conti_id: '' })} />
                        <span className="mx-1" />
                        {[['uscita', 'Uscita', '#e03131'], ['entrata', 'Entrata', '#2f9e44'], ['giroconto', 'Giroconto', '#868e96']].map(bottone)}
                      </div>
                      <div className="d-flex flex-wrap gap-2 align-items-center">
                        <span className="text-muted" style={{ fontSize: 13 }}>Con i soci:</span>
                        {[['finanziamento_socio', 'Anticipo del socio', '#e8590c'], ['rimborso_socio', 'Rimborso socio', '#d9480f'], ['restituzione_socio', 'Restituzione socio', '#1c7ed6'],
                          ['conferimento_socio', 'Conferimento', '#0b7285'], ['prelievo_utili', 'Prelievo del socio', '#862e9c']].map(bottone)}
                      </div>
                    </>
                  );
                })()}
              </div>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Label>{isTrasf(form.segno) ? 'Da rapporto' : 'Rapporto usato'}</Form.Label>
                  <Form.Select required value={form.banca_id} onChange={(e) => setForm({ ...form, banca_id: e.target.value })}>
                    <option value="">— scegli —</option>
                    {rapporti.filter((r) => !isSoci(form.segno) || r.scope === SCOPE_ORIGINE[form.segno])
                      .map((r) => <option key={r.id} value={r.id}>{r.scope === 'azienda' ? '🏢' : '🏠'} {r.denominazione}</option>)}
                  </Form.Select>
                </Col>
                {isTrasf(form.segno) && (
                  <Col md={6}>
                    <Form.Label>A rapporto</Form.Label>
                    <Form.Select required value={form.banca_destinazione_id} onChange={(e) => setForm({ ...form, banca_destinazione_id: e.target.value })}>
                      <option value="">— scegli —</option>
                      {rapporti.filter((r) => String(r.id) !== String(form.banca_id)
                        && (!isSoci(form.segno) || r.scope === SCOPE_DESTINAZIONE[form.segno])).map((r) => (
                        <option key={r.id} value={r.id}>{r.scope === 'azienda' ? '🏢' : '🏠'} {r.denominazione}</option>
                      ))}
                    </Form.Select>
                  </Col>
                )}
                <Col md={3}><Form.Label>Data</Form.Label><Form.Control type="date" required value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} /></Col>
                <Col md={3}><Form.Label>Importo (€)</Form.Label><Form.Control type="number" step="0.01" min="0.01" required value={form.importo} onChange={(e) => setForm({ ...form, importo: e.target.value })} /></Col>
                <Col md={isTrasf(form.segno) ? 6 : 12}>
                  <Form.Label>Descrizione</Form.Label>
                  <Form.Control required list="suggerimenti-descrizioni" autoComplete="off" value={form.descrizione}
                    onChange={(e) => cambiaDescrizione(e.target.value)} />
                  {formNuovo && (
                    <datalist id="suggerimenti-descrizioni">
                      {suggerimenti.map((s) => <option key={s.descrizione} value={s.descrizione}>{s.volte} volte</option>)}
                    </datalist>
                  )}
                </Col>
                {!isTrasf(form.segno) && (
                  <>
                    <Col md={6}><Form.Label>Controparte <span className="text-muted">(facoltativa)</span></Form.Label>
                      <Form.Control value={form.controparte} onChange={(e) => setForm({ ...form, controparte: e.target.value })} /></Col>
                    <Col md={6}>
                      <Form.Label>Voce del piano dei conti</Form.Label>
                      <Form.Select value={form.piano_conti_id} onChange={(e) => setForm({ ...form, piano_conti_id: e.target.value })}>
                        <option value="">— scegli dopo —</option>
                        {(piano[form.scope] || []).filter((v) => (form.segno === 'entrata' ? v.tipo === 'entrata' : v.tipo === 'spesa')).map((v) => (
                          <option key={v.id} value={v.id}>{v.nome}</option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col md={6}><Form.Label>Commissione (€) <span className="text-muted">(facoltativa)</span></Form.Label>
                      <Form.Control type="number" step="0.01" min="0" value={form.commissione} onChange={(e) => setForm({ ...form, commissione: e.target.value })} /></Col>
                  </>
                )}
                {((servePersona && !isTrasf(form.segno)) || isSoci(form.segno)) && (
                  <Col md={6}>
                    <Form.Label>{isSoci(form.segno) ? 'Socio' : 'Persona'}</Form.Label>
                    <Form.Select required={isSoci(form.segno) || (bancaForm && bancaForm.scope !== form.scope)} value={form.persona} onChange={(e) => setForm({ ...form, persona: e.target.value })}>
                      <option value="">—</option>
                      {PERSONE.filter((p) => !isSoci(form.segno) || p !== 'Comune').map((p) => <option key={p} value={p}>{p}</option>)}
                    </Form.Select>
                  </Col>
                )}
                <Col md={12}><Form.Label>Note</Form.Label><Form.Control as="textarea" rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></Col>
              </Row>
              {bancaForm && bancaForm.scope !== form.scope && !isTrasf(form.segno) && (
                <Alert variant="info" className="mt-3 mb-0" style={{ fontSize: 13 }}>
                  {form.scope === 'personale'
                    ? 'Spesa personale pagata con un rapporto aziendale: nell’azienda diventa un prelievo del socio (con la scrittura in Prima Nota), nel personale una spesa.'
                    : 'Costo aziendale pagato con un rapporto personale: nell’azienda risulta un anticipo del socio (con la scrittura in Prima Nota), nel personale un credito verso ModenaCinema.'}
                  {form.scope === 'azienda' && (
                    <Form.Check className="mt-2" type="switch" id="ha-fattura"
                      label="Ha già una fattura passiva registrata (non contarlo di nuovo nel registro cassa)"
                      checked={!!form.ha_fattura} onChange={(e) => setForm({ ...form, ha_fattura: e.target.checked })} />
                  )}
                </Alert>
              )}
              {isSoci(form.segno) && (
                <Alert variant="info" className="mt-3 mb-0" style={{ fontSize: 13 }}>
                  {SPIEGA_SOCI[form.segno]}
                  {form.persona && saldiSoci && saldiSoci[form.persona] && (
                    <div className="mt-1" style={{ fontWeight: 600 }}>
                      L’azienda deve a {form.persona}: {euro(saldiSoci[form.persona].azienda_deve)} · {form.persona} deve all’azienda: {euro(saldiSoci[form.persona].socio_deve)} · Apporti versati da {form.persona}: {euro(saldiSoci[form.persona].apporti)}
                    </div>
                  )}
                </Alert>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setForm(null)}>Annulla</Button>
              <Button type="submit" disabled={salvataggio} style={{ background: SCOPE_TEMA[form.scope].colore, borderColor: SCOPE_TEMA[form.scope].colore }}>
                {salvataggio ? 'Salvo…' : 'Salva'}
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Modal>

      {/* ---- Assegna voce e persona a piu' movimenti ---- */}
      <Modal show={!!bulk} onHide={() => setBulk(null)} centered>
        {bulk && (() => {
          const scopeRighe = [...new Set(bulk.noti.map((x) => x.scope))];
          const scopeEffettivo = bulk.scope || (scopeRighe.length === 1 ? scopeRighe[0] : null);
          const segni = [...new Set(bulk.noti.map((x) => x.segno))];
          const tipoVoce = segni.length === 1 ? (segni[0] === 'entrata' ? 'entrata' : 'spesa') : null;
          const vociDisponibili = scopeEffettivo ? (piano[scopeEffettivo] || []).filter((v) => !tipoVoce || v.tipo === tipoVoce) : [];
          return (
            <Form onSubmit={salvaBulk}>
              <Modal.Header closeButton><Modal.Title>Assegna a {selezione.length} movimenti</Modal.Title></Modal.Header>
              <Modal.Body>
                <p className="text-muted" style={{ fontSize: 13 }}>
                  Imposti una volta sola ciò che vale per tutti i movimenti selezionati. I campi che lasci vuoti restano come sono.
                </p>
                <Form.Label>Contabilità</Form.Label>
                <div className="d-flex gap-2 mb-3">
                  {[['', 'Invariata'], ['azienda', 'Azienda'], ['personale', 'Personale']].map(([k, l]) => (
                    <Button key={k || 'inv'} size="sm" variant={bulk.scope === k ? 'primary' : 'outline-secondary'}
                      onClick={(e) => { e.preventDefault(); setBulk({ ...bulk, scope: k, voce: '' }); }}>{l}</Button>
                  ))}
                </div>
                <Form.Label>Voce del piano dei conti</Form.Label>
                <Form.Select className="mb-3" value={bulk.voce} disabled={!scopeEffettivo} onChange={(e) => setBulk({ ...bulk, voce: e.target.value })}>
                  <option value="">{scopeEffettivo ? '— non cambiare —' : '— scegli prima la contabilità —'}</option>
                  {vociDisponibili.map((v) => <option key={v.id} value={v.id}>{v.nome}</option>)}
                </Form.Select>
                <Form.Label>Persona</Form.Label>
                <Form.Select value={bulk.persona} onChange={(e) => setBulk({ ...bulk, persona: e.target.value })}>
                  <option value="">— non cambiare —</option>
                  {PERSONE.map((p) => <option key={p} value={p}>{p}</option>)}
                </Form.Select>
                <div className="text-muted mt-3" style={{ fontSize: 12 }}>
                  Un movimento personale diventa «pronto da registrare» solo quando ha sia la voce sia la persona. I movimenti già registrati, ignorati o i giroconti non vengono toccati.
                </div>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setBulk(null)}>Annulla</Button>
                <Button type="submit" disabled={!bulk.scope && !bulk.voce && !bulk.persona} style={{ background: tema.colore, borderColor: tema.colore }}>Assegna</Button>
              </Modal.Footer>
            </Form>
          );
        })()}
      </Modal>

      {/* ---- Converti in giroconto / spostamento tra rapporti ---- */}
      <Modal show={!!converti} onHide={() => setConverti(null)} centered>
        {converti && (() => {
          const m = converti.m;
          const altra = rapportoPer[Number(converti.altra)];
          const da = m.segno === 'uscita' ? rapportoPer[m.banca_id] : altra;
          const a = m.segno === 'uscita' ? altra : rapportoPer[m.banca_id];
          const tra = da && a ? (da.scope === a.scope ? 'giroconto' : da.scope === 'azienda' ? 'da_azienda' : 'da_personale') : null;
          const scelte = tra === 'da_azienda'
            ? [['rimborso', 'rimborso_socio', 'Rimborso di anticipi'], ['prelievo', 'prelievo_utili', 'Prelievo del socio (denaro dell’azienda a lui)']]
            : [['anticipo', 'finanziamento_socio', 'Anticipo del socio (prestito all’azienda)'], ['restituzione', 'restituzione_socio', 'Restituzione di prelievi'], ['conferimento', 'conferimento_socio', 'Conferimento (capitale)']];
          const spiegazione = {
            giroconto: 'Giroconto: sposta denaro tra due tuoi rapporti, non entra nel bilancio come entrata o spesa.',
          };
          const naturaCorrente = converti.natura || (tra === 'da_personale' ? 'anticipo' : '');
          const sceltaFatta = scelte.find(([n]) => n === naturaCorrente);
          const socio = converti.persona && saldiSoci ? saldiSoci[converti.persona] : null;
          return (
            <Form onSubmit={async (e) => {
              e.preventDefault();
              try {
                if (tra && tra !== 'giroconto' && !naturaCorrente) { toast.error('Scegli che cos’è questo movimento'); return; }
if (converti.multipli) {
                  const r = await convertiMovimentiMultipli({ ids: converti.multipli.map((x) => x.id), altra_banca_id: Number(converti.altra), persona: converti.persona || null, natura: naturaCorrente || null });
                  toast.success(`${r.convertiti} movimenti convertiti${r.ignorati ? `, ${r.ignorati} altre parti ignorate` : ''}`);
                  (r.errori || []).slice(0, 3).forEach((x) => toast.error(`#${x.id}: ${x.errore}`, { duration: 7000 }));
                } else {
                  const r = await convertiMovimento(m.id, { altra_banca_id: Number(converti.altra), persona: converti.persona || null, natura: naturaCorrente || null });
                  toast.success(r.ignorati ? 'Convertito: l’altra parte dello spostamento è stata ignorata' : 'Movimento convertito');
                }
                setConverti(null);
                ricarica();
              } catch (err) {
                toast.error(messaggioErrore(err));
              }
            }}>
              <Modal.Header closeButton><Modal.Title>{converti.multipli ? `Converti ${converti.multipli.length} movimenti in spostamento` : 'Converti in spostamento tra rapporti'}</Modal.Title></Modal.Header>
              <Modal.Body>
                <p className="text-muted" style={{ fontSize: 14 }}>
                  {converti.multipli ? (
                    <>
                      <strong>{converti.multipli.length} movimenti</strong> · totale <strong>{euro(converti.multipli.reduce((t, x) => t + x.importo, 0))}</strong> {m.segno === 'uscita' ? 'in uscita da' : 'in entrata su'} <strong>{m.banca}</strong>
                      <br />{converti.multipli.slice(0, 3).map((x) => x.descrizione).join(' · ')}{converti.multipli.length > 3 ? ' …' : ''}
                    </>
                  ) : (
                    <>
                      {dataIt(m.data)} · <strong>{euro(m.importo)}</strong> {m.segno === 'uscita' ? 'in uscita da' : 'in entrata su'} <strong>{m.banca}</strong>
                      <br />{m.descrizione}
                    </>
                  )}
                </p>
                <Form.Label>{m.segno === 'uscita' ? 'Verso quale rapporto?' : 'Da quale rapporto?'}</Form.Label>
                <Form.Select required value={converti.altra} onChange={(e) => setConverti({ ...converti, altra: e.target.value })}>
                  <option value="">— scegli —</option>
                  {rapporti.filter((r) => r.id !== m.banca_id).map((r) => (
                    <option key={r.id} value={r.id}>{r.scope === 'azienda' ? '🏢' : '🏠'} {r.denominazione}</option>
                  ))}
                </Form.Select>
                {tra === 'giroconto' && <Alert variant="info" className="mt-3 mb-0" style={{ fontSize: 13 }}>{spiegazione.giroconto}</Alert>}
                {tra && tra !== 'giroconto' && (
                  <div className="mt-3">
                    <Form.Label>Che cos’è questo movimento?</Form.Label>
                    <div className="d-grid gap-2">
                      {scelte.map(([n, segnoScelto, etichetta]) => (
                        <label key={n} style={{ border: naturaCorrente === n ? '2px solid #3b5bdb' : '1px solid #ced4da', borderRadius: 10, padding: '8px 12px', cursor: 'pointer', background: naturaCorrente === n ? '#edf2ff' : '#fff' }}>
                          <Form.Check type="radio" name="natura" id={`natura-${n}`} checked={naturaCorrente === n}
                            onChange={() => setConverti({ ...converti, natura: n })}
                            label={<span><strong>{etichetta}</strong><br /><span className="text-muted" style={{ fontSize: 12 }}>{SPIEGA_SOCI[segnoScelto]}</span></span>} />
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                {tra && tra !== 'giroconto' && (
                  <div className="mt-3">
                    <Form.Label>Socio <span className="text-muted" style={{ fontSize: 12 }}>(il titolare del conto personale)</span></Form.Label>
                    <Form.Select required value={converti.persona} onChange={(e) => setConverti({ ...converti, persona: e.target.value })}>
                      <option value="">— scegli —</option>
                      <option value="Giorgio">Giorgio</option>
                      <option value="Francesca">Francesca</option>
                    </Form.Select>
                    {socio && (
                      <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                        Oggi: l’azienda deve a {converti.persona} {euro(socio.azienda_deve)} · {converti.persona} deve all’azienda {euro(socio.socio_deve)} · apporti già versati da {converti.persona} {euro(socio.apporti)}
                        {sceltaFatta && sceltaFatta[0] === 'restituzione' && socio.socio_deve === 0 ? ' (non ci sono prelievi da restituire: forse è un anticipo)' : ''}
                      </div>
                    )}
                  </div>
                )}
                <div className="text-muted mt-3" style={{ fontSize: 12 }}>
                  Se l’altra parte dello stesso spostamento è già tra i movimenti (stesso importo, pochi giorni di distanza), viene ignorata per non contarlo due volte.
                </div>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setConverti(null)}>Annulla</Button>
                <Button type="submit" disabled={!converti.altra} style={{ background: tema.colore, borderColor: tema.colore }}>Converti</Button>
              </Modal.Footer>
            </Form>
          );
        })()}
      </Modal>

      {/* ---- Dividi / rettifica in più quote ---- */}
      <Modal show={!!dividi} onHide={() => setDividi(null)} centered>
        {dividi && (() => {
          const m = dividi.m;
          const somma = dividi.parti.reduce((t, p) => t + (parseFloat(String(p.importo).replace(',', '.')) || 0), 0);
          const resto = Math.round((dividi.totale - somma) * 100) / 100;
          const voci = (piano[m.scope] || []).filter((v) => (m.segno === 'entrata' ? v.tipo === 'entrata' : v.tipo === 'spesa'));
          const cambia = (i, campo, valore) => setDividi({ ...dividi, parti: dividi.parti.map((p, j) => (j === i ? { ...p, [campo]: valore } : p)) });
          return (
            <Form onSubmit={async (e) => {
              e.preventDefault();
              try {
                const parti = dividi.parti.filter((p) => p.importo !== '').map((p) => ({
                  importo: parseFloat(String(p.importo).replace(',', '.')),
                  persona: p.persona || null,
                  piano_conti_id: !isTrasf(m.segno) && p.piano_conti_id ? Number(p.piano_conti_id) : null,
                }));
                await dividiMovimento(m.id, parti);
                toast.success(dividi.registrati ? 'Movimento rettificato e registrato di nuovo' : 'Movimento diviso');
                setDividi(null);
                ricarica();
              } catch (err) {
                toast.error(messaggioErrore(err));
              }
            }}>
              <Modal.Header closeButton><Modal.Title>Dividi il movimento</Modal.Title></Modal.Header>
              <Modal.Body>
                <p className="text-muted" style={{ fontSize: 14 }}>
                  {dataIt(m.data)} · <strong>{euro(dividi.totale)}</strong> · {m.banca}{isTrasf(m.segno) ? ` → ${m.banca_destinazione}` : ''}
                  <br />{m.descrizione}
                </p>
                {isSoci(m.segno) && (
                  <Alert variant="info" style={{ fontSize: 13 }}>
                    Per ogni quota indica <strong>a chi fa credito l’azienda</strong>: la parte usata per ModenaCinema è un anticipo di Francesca,
                    quella che hai usato tu personalmente è un anticipo tuo (finanziato con i soldi di Francesca).
                  </Alert>
                )}
                {dividi.parti.map((p, i) => (
                  <Row className="g-2 mb-2 align-items-end" key={i}>
                    <Col xs={4}>
                      {i === 0 && <Form.Label>Importo (€)</Form.Label>}
                      <Form.Control type="number" step="0.01" min="0.01" value={p.importo} onChange={(e) => cambia(i, 'importo', e.target.value)} />
                    </Col>
                    <Col xs={isTrasf(m.segno) ? 6 : 3}>
                      {i === 0 && <Form.Label>Persona</Form.Label>}
                      <Form.Select value={p.persona} onChange={(e) => cambia(i, 'persona', e.target.value)}>
                        <option value="">—</option>
                        {(isSoci(m.segno) ? ['Giorgio', 'Francesca'] : PERSONE).map((x) => <option key={x} value={x}>{x}</option>)}
                      </Form.Select>
                    </Col>
                    {!isTrasf(m.segno) && (
                      <Col xs={3}>
                        {i === 0 && <Form.Label>Voce</Form.Label>}
                        <Form.Select value={p.piano_conti_id} onChange={(e) => cambia(i, 'piano_conti_id', e.target.value)}>
                          <option value="">—</option>
                          {voci.map((v) => <option key={v.id} value={v.id}>{v.nome}</option>)}
                        </Form.Select>
                      </Col>
                    )}
                    <Col xs={2}>
                      {dividi.parti.length > 1 && (
                        <Button size="sm" variant="light" title="Togli questa quota" onClick={() => setDividi({ ...dividi, parti: dividi.parti.filter((_, j) => j !== i) })}>
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      )}
                    </Col>
                  </Row>
                ))}
                <div className="d-flex justify-content-between align-items-center mt-2">
                  <Button size="sm" variant="outline-secondary"
                    onClick={() => setDividi({ ...dividi, parti: [...dividi.parti, { importo: resto > 0 ? String(resto) : '', persona: '', piano_conti_id: '' }] })}>
                    <FontAwesomeIcon icon={faPlus} /> Aggiungi quota
                  </Button>
                  <span style={{ fontWeight: 600, color: resto === 0 ? '#2f9e44' : '#e03131' }}>
                    {resto === 0 ? 'Quadra' : resto > 0 ? `Mancano ${euro(resto)}` : `Eccedono ${euro(-resto)}`}
                  </span>
                </div>
                {dividi.registrati && (
                  <Alert variant="warning" className="mt-3 mb-0" style={{ fontSize: 13 }}>
                    Il movimento è già registrato: salvando, la registrazione su Firefly e in Prima Nota viene annullata e rifatta con le nuove quote.
                  </Alert>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setDividi(null)}>Annulla</Button>
                <Button type="submit" disabled={resto !== 0 || dividi.parti.some((p) => p.importo !== '' && !(parseFloat(String(p.importo).replace(',', '.')) > 0))}
                  style={{ background: tema.colore, borderColor: tema.colore }}>Salva</Button>
              </Modal.Footer>
            </Form>
          );
        })()}
      </Modal>

      {/* ---- Importazione da file ---- */}
      <Modal show={!!importa} onHide={() => setImporta(null)} centered size="lg">
        {importa && (
          <>
            <Modal.Header closeButton><Modal.Title>Importa movimenti da file</Modal.Title></Modal.Header>
            <Modal.Body>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Label>Rapporto</Form.Label>
                  <Form.Select value={importa.bancaId} onChange={(e) => {
                    const r = rapportoPer[Number(e.target.value)];
                    const nome = (r?.denominazione || '').toLowerCase();
                    const sug = r?.tipo === 'paypal' || nome.includes('paypal') ? 'PayPal' : nome.includes('pos') ? 'myPOS'
                      : nome.includes('isy') ? 'Isybank' : nome.includes('ing') ? 'ING' : nome.includes('sumup') ? 'SumUp' : '';
                    setImporta({ ...importa, bancaId: e.target.value, fonte: sug || importa.fonte, risultato: null });
                  }}>
                    <option value="">— scegli —</option>
                    {rapporti.map((r) => <option key={r.id} value={r.id}>{r.scope === 'azienda' ? '🏢' : '🏠'} {r.denominazione}</option>)}
                  </Form.Select>
                </Col>
                <Col md={6}>
                  <Form.Label>Formato del file</Form.Label>
                  <Form.Select value={importa.fonte} onChange={(e) => setImporta({ ...importa, fonte: e.target.value, risultato: null })}>
                    <option value="">— scegli —</option>
                    {formati.map((f) => <option key={f.codice} value={f.codice}>{f.nome}</option>)}
                  </Form.Select>
                </Col>
                <Col md={12}>
                  <Form.Control type="file" accept=".csv,.xlsx,.xls" onChange={(e) => setImporta({ ...importa, file: e.target.files[0] || null, risultato: null })} />
                </Col>
              </Row>
              {importa.risultato && (
                <div className="mt-3">
                  <div className="d-flex gap-3 mb-2 flex-wrap">
                    <Badge bg="success">{importa.risultato.nuovi} nuovi</Badge>
                    <Badge bg="secondary">{importa.risultato.gia_importati} già importati</Badge>
                    <Badge bg="warning" text="dark">{importa.risultato.saltati} saltati</Badge>
                    {importa.risultato.possibili_fatture > 0 && (
                      <Badge bg="info">{importa.risultato.possibili_fatture} forse già registrati come fattura</Badge>
                    )}
                  </div>
                  {importa.risultato.possibili_fatture > 0 && (
                    <Alert variant="info" className="py-2" style={{ fontSize: 13 }}>
                      Le righe segnate «forse fattura» hanno lo stesso importo e una data vicina a una fattura passiva già registrata su Firefly:
                      le ho deselezionate per non creare un doppione. Se non è la stessa spesa, spunta di nuovo la riga.
                    </Alert>
                  )}
                  {importa.risultato.saltati > 0 && (
                    <div className="text-muted mb-1" style={{ fontSize: 12 }}>
                      Le righe «saltato» sono normalmente gestite altrove (ad esempio i bonifici Nuvei dalla Cassa IBS): spunta quelle che vuoi importare lo stesso. I bonifici Nuvei entrano come giroconto dal conto «MC - TRANSITO IBS» a questo rapporto, già pronti da registrare.
                    </div>
                  )}
                  <div className="text-muted mb-1" style={{ fontSize: 12 }}>
                    Togli la spunta alle righe che non vuoi importare: restano salvate nella scheda «Ignorati», così non ricompaiono se riimporti lo stesso file.
                  </div>
                  <div style={{ maxHeight: 280, overflow: 'auto', border: '1px solid #dee2e6', borderRadius: 8 }}>
                    <Table size="sm" className="mb-0" style={{ fontSize: 13 }}>
                      <thead style={{ position: 'sticky', top: 0, background: '#f8f9fa' }}>
                        <tr>
                          <th style={{ width: 30 }}>
                            <Form.Check
                              checked={nuoveRighe.length > 0 && importa.esclusi.length === 0}
                              onChange={(e) => setImporta((i) => ({ ...i, esclusi: e.target.checked ? [] : nuoveRighe.map((r) => r.external_id) }))}
                              title="Seleziona o deseleziona tutte" />
                          </th>
                          <th>Data</th><th>Movimento</th><th className="text-end">Importo</th><th>Esito</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importa.risultato.anteprima.map((r, i) => (
                          <tr key={i} style={{ opacity: r.esito === 'saltato' && importa.inclusi.includes(r.external_id) ? 1 : r.esito !== 'nuovo' ? 0.5 : importa.esclusi.includes(r.external_id) ? 0.55 : 1 }}>
                            <td>{r.esito === 'saltato' && (
                              <Form.Check title="Importa lo stesso questa riga" checked={importa.inclusi.includes(r.external_id)}
                                onChange={(e) => setImporta((im) => ({ ...im, inclusi: e.target.checked ? [...im.inclusi, r.external_id] : im.inclusi.filter((x) => x !== r.external_id) }))} />
                            )}{r.esito === 'nuovo' && (
                              <Form.Check checked={!importa.esclusi.includes(r.external_id)}
                                onChange={(e) => setImporta((im) => ({ ...im, esclusi: e.target.checked ? im.esclusi.filter((x) => x !== r.external_id) : [...im.esclusi, r.external_id] }))} />
                            )}</td>
                            <td>{dataIt(r.data)}</td>
                            <td style={{ maxWidth: 360, whiteSpace: 'normal', wordBreak: 'break-word' }} title={r.motivo || r.dettaglio || r.descrizione}>
                              {r.descrizione}
                              {r.possibile_fattura && (
                                <div style={{ fontSize: 11, color: '#0c8599' }}>
                                  forse fattura n. {r.possibile_fattura.numero}{r.possibile_fattura.fornitore ? ` · ${r.possibile_fattura.fornitore}` : ''}
                                </div>
                              )}
                              {r.ordini_registrati && <div style={{ fontSize: 11, color: '#0c8599' }}>già registrato con l’ordine n. {r.ordini_registrati.join(', ')}: deselezionato</div>}
                              {r.possibile_giroconto && <div style={{ fontSize: 11, color: '#1971c2' }}>forse giroconto ({r.possibile_giroconto})</div>}
                              {r.giroconto_da && <div style={{ fontSize: 11, color: '#1971c2' }}>entra come giroconto {r.giroconto_verso ? 'verso' : 'da'} {r.giroconto_da}</div>}
                              {r.voce && <div style={{ fontSize: 11, color: '#0c8599' }}>voce: {r.voce}</div>}
                            </td>
                            <td className="text-end" style={{ color: r.importo < 0 ? '#e03131' : '#2f9e44' }}>{euro(r.importo)}</td>
                            <td>{r.esito === 'nuovo' ? <Badge bg="success">nuovo</Badge> : r.esito === 'gia_importato' ? <Badge bg="secondary">già importato</Badge> : <Badge bg="warning" text="dark">saltato</Badge>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setImporta(null)}>Chiudi</Button>
              <Button variant="outline-primary" disabled={!importa.bancaId || !importa.fonte || !importa.file || importa.lavoro}
                onClick={async () => {
                  setImporta((i) => ({ ...i, lavoro: true }));
                  try {
                    const r = await importaMovimenti({ bancaId: importa.bancaId, fonte: importa.fonte, file: importa.file, conferma: false });
                    const esclusiIniziali = r.anteprima.filter((a) => a.esito === 'nuovo' && (a.possibile_fattura || a.ordini_registrati)).map((a) => a.external_id);
                    setImporta((i) => ({ ...i, risultato: r, esclusi: esclusiIniziali, lavoro: false }));
                  } catch (e) {
                    toast.error(messaggioErrore(e, 'File non leggibile'));
                    setImporta((i) => ({ ...i, lavoro: false }));
                  }
                }}>
                {importa.lavoro ? 'Leggo…' : 'Anteprima'}
              </Button>
              <Button disabled={!importa.risultato || importa.risultato.nuovi === 0 || nuoveRighe.length + importa.inclusi.length === importa.esclusi.length || importa.lavoro} style={{ background: tema.colore, borderColor: tema.colore }}
                onClick={async () => {
                  setImporta((i) => ({ ...i, lavoro: true }));
                  try {
                    const r = await importaMovimenti({ bancaId: importa.bancaId, fonte: importa.fonte, file: importa.file, conferma: true, ignora: importa.esclusi, includi: importa.inclusi });
                    toast.success(`${r.nuovi - r.ignorati} movimenti importati${r.ignorati ? `, ${r.ignorati} messi tra gli ignorati` : ''}: ora classificali`);
                    setImporta(null);
                    setTab('da_classificare');
                    ricarica();
                  } catch (e) {
                    toast.error(messaggioErrore(e, 'Importazione non riuscita'));
                    setImporta((i) => ({ ...i, lavoro: false }));
                  }
                }}>
                Importa {importa.risultato ? nuoveRighe.length - importa.esclusi.length + importa.inclusi.length : ''} movimenti
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </>
  );
};

export default Movimenti;
